import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, UserPlus, Edit, Copy, RefreshCw, Check, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";

export default function UsersManagement() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  
  // Form state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"operator" | "admin" | "supervisor">("operator");
  const [department, setDepartment] = useState<"water" | "wastewater" | "traffic">("water");
  const [operatorInitial, setOperatorInitial] = useState("");
  const [isActive, setIsActive] = useState(true);
  
  const [createMethod, setCreateMethod] = useState<"invite" | "manual">("invite");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  
  const { data: users, refetch } = trpc.users.list.useQuery();
  const createInviteMutation = trpc.users.createInvite.useMutation();
  const createManualUserMutation = trpc.users.createUserManually.useMutation();
  const updateMutation = trpc.users.update.useMutation();
  const regenerateInviteMutation = trpc.users.regenerateInvite.useMutation();
  const deleteMutation = trpc.users.delete.useMutation();

  // Helper function to copy invite URL with fallback for environments where Clipboard API is not supported (e.g. non-HTTPS, older browsers, some electron shells).
  const copyInviteUrlToClipboard = async (inviteUrl: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied to clipboard!");
      return true;
    } catch (error) {
      // Fallback to textarea method
      try {
        const textarea = document.createElement('textarea');
        textarea.value = inviteUrl;
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (successful) {
          toast.success("Invite link copied to clipboard!");
          return true;
        } else {
          throw new Error();
        }
      } catch (fallbackError) {
        console.error("Copy invite link failed", fallbackError || error);
        toast.error("Failed to copy invite link");
        return false;
      }
    }
  };
  
  if (user?.role !== 'admin' && user?.role !== 'supervisor') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">Supervisor or Admin access required</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const resetForm = () => {
    setEmail("");
    setName("");
    setRole("operator");
    setDepartment("water");
    setOperatorInitial("");
    setIsActive(true);
    setEditingUser(null);
    setCreateMethod("invite");
  };
  
  const openCreateDialog = (method: "invite" | "manual" = "invite") => {
    resetForm();
    setCreateMethod(method);
    setIsDialogOpen(true);
  };
  
  const openEditDialog = (user: any) => {
    setEditingUser(user);
    setEmail(user.email || "");
    setName(user.name || "");
    setRole(user.role);
    setDepartment(user.department || "water");
    setOperatorInitial(user.operatorInitial || "");
    setIsActive(user.isActive);
    setIsDialogOpen(true);
  };
  
  const handleSave = async () => {
    if (editingUser) {
      // Update existing user
      try {
        await updateMutation.mutateAsync({
          userId: editingUser.id,
          role,
          department: (role === 'operator' || role === 'supervisor') ? department : undefined,
          operatorInitial: operatorInitial || undefined,
          isActive,
        });
        toast.success("User updated successfully");
        refetch();
        setIsDialogOpen(false);
        resetForm();
      } catch (error: any) {
        toast.error(error.message || "Failed to update user");
      }
    } else {
      // Create new user (invite or manual)
      if (!email.trim() || !name.trim()) {
        toast.error("Email and name are required");
        return;
      }
      
      try {
        if (createMethod === "manual") {
          // Create user manually without invite
          await createManualUserMutation.mutateAsync({
            email,
            name,
            role,
            department: (role === 'operator' || role === 'supervisor') ? department : undefined,
            operatorInitial: operatorInitial || undefined,
          });
          toast.success("User created successfully. They can now log in using their email.");
        } else {
          // Create invite
          const result = await createInviteMutation.mutateAsync({
            email,
            name,
            role,
            department: (role === 'operator' || role === 'supervisor') ? department : undefined,
            operatorInitial: operatorInitial || undefined,
          });
          toast.success("Invite created successfully");
          
          // Copy invite link to clipboard
          if (result.inviteUrl) {
            await navigator.clipboard.writeText(result.inviteUrl);
            toast.success("Invite link copied to clipboard");
          }
        }
        
        refetch();
        setIsDialogOpen(false);
        resetForm();
      } catch (error: any) {
        toast.error(error.message || `Failed to ${createMethod === "manual" ? "create user" : "create invite"}`);
      }
    }
  };
  
  const handleCopyInviteLink = async (user: any) => {
    if (!user.inviteToken) return;

    // Get config from window (injected by server) or fallback to env vars
    const authProvider = import.meta.env.VITE_AUTH_PROVIDER || 'manus';
    const customDomain = (window as any).__APP_CONFIG__?.customDomain;

    // For custom auth, always use local domain. For manus, use oauth portal
    let inviteUrl: string;
    if (authProvider === 'custom') {
      inviteUrl = customDomain
        ? `https://${customDomain}/invite/${user.inviteToken}`
        : `${window.location.origin}/invite/${user.inviteToken}`;
    } else {
      const oauthPortalUrl = (window as any).__APP_CONFIG__?.oauthPortalUrl || import.meta.env.VITE_OAUTH_PORTAL_URL || 'https://oauth.manus.im';
      const appId = (window as any).__APP_CONFIG__?.appId || import.meta.env.VITE_APP_ID;
      inviteUrl = customDomain
        ? `https://${customDomain}/invite/${user.inviteToken}`
        : `${oauthPortalUrl}/login?app_id=${appId}&invite=${user.inviteToken}`;
    }

    const success = await copyInviteUrlToClipboard(inviteUrl);
    if (success) {
      setCopiedToken(user.inviteToken);
      setTimeout(() => setCopiedToken(null), 2000);
    }
  };
  
  const handleRegenerateInvite = async (userId: number) => {
    try {
      const result = await regenerateInviteMutation.mutateAsync({ userId });
      toast.success("Invite link regenerated");
      
      // Copy new invite link to clipboard
      if (result.inviteUrl) {
        await navigator.clipboard.writeText(result.inviteUrl);
        toast.success("New invite link copied to clipboard");
      }
      
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Failed to regenerate invite");
    }
  };
  
  const openDeleteDialog = (userToDelete: any) => {
    setUserToDelete(userToDelete);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteMutation.mutateAsync({ userId: userToDelete.id });
      toast.success(`User ${userToDelete.name} deleted successfully`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete user");
    }
  };
  
  const getStatusBadge = (user: any) => {
    if (!user.isActive) {
      return <Badge variant="destructive">Disabled</Badge>;
    }
    if (user.isPending) {
      return <Badge variant="secondary">Invited / Pending</Badge>;
    }
    if (!user.openId || !user.lastSignedIn) {
      return <Badge variant="secondary">Invited / Pending</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };
  
  const formatDate = (date: any) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString();
  };
  
  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage user invites and permissions</p>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Users</CardTitle>
              <CardDescription>Create invites and manage user access</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => openCreateDialog("invite")} variant="outline">
                <UserPlus className="mr-2 h-4 w-4" />
                Create Invite
              </Button>
              <Button onClick={() => openCreateDialog("manual")}>
                <UserPlus className="mr-2 h-4 w-4" />
                Create User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users?.map((listUser: any) => (
              <div key={listUser.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{listUser.name || "Unnamed User"}</span>
                    {getStatusBadge(listUser)}
                    <Badge variant="outline">{listUser.role}</Badge>
                    {listUser.department && (
                      <Badge variant="secondary" className="capitalize">{listUser.department}</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {listUser.email || "No email"}
                    {listUser.operatorInitial && ` • Operator: ${listUser.operatorInitial}`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last sign-in: {formatDate(listUser.lastSignedIn)}
                    {(listUser.isPending || !listUser.openId) && listUser.invitedAt && ` • Invited: ${formatDate(listUser.invitedAt)}`}
                    {(listUser.isPending || !listUser.openId) && !listUser.invitedAt && ` • Created: ${formatDate(listUser.createdAt)}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(listUser.isPending || !listUser.openId) && listUser.inviteToken && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyInviteLink(listUser)}
                      >
                        {copiedToken === listUser.inviteToken ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateInvite(listUser.id)}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(listUser)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {listUser.id !== user?.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteDialog(listUser)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {users?.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No users found. Create an invite to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser 
                ? "Edit User" 
                : createMethod === "manual" 
                  ? "Create User Manually" 
                  : "Create User Invite"}
            </DialogTitle>
            <DialogDescription>
              {editingUser 
                ? "Update user role, operator initial, and status"
                : createMethod === "manual"
                  ? "Create a user account directly without sending an invite link"
                  : "Enter user details to generate an invite link"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!editingUser}
                placeholder="user@example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!!editingUser}
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={(value: "operator" | "admin" | "supervisor") => setRole(value)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(role === 'operator' || role === 'supervisor') && (
              <div>
                <Label htmlFor="department">Department *</Label>
                <Select value={department} onValueChange={(value: "water" | "wastewater" | "traffic") => setDepartment(value)}>
                  <SelectTrigger id="department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="water">Water</SelectItem>
                    <SelectItem value="wastewater">Wastewater</SelectItem>
                    <SelectItem value="traffic">Traffic</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Required for operators and supervisors
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="operatorInitial">Operator Initial (Optional)</Label>
              <Input
                id="operatorInitial"
                value={operatorInitial}
                onChange={(e) => setOperatorInitial(e.target.value)}
                placeholder="JD"
                maxLength={10}
              />
            </div>
            
            {editingUser && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="active">Active</Label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingUser 
                ? "Update" 
                : createMethod === "manual" 
                  ? "Create User" 
                  : "Create Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
